import { Check, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/hooks/useLanguage';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/config';
import { cn } from '@/lib/utils';

interface LanguageSwitcherProps {
  className?: string;
  variant?: 'default' | 'compact';
}

export function LanguageSwitcher({ className, variant = 'default' }: LanguageSwitcherProps) {
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslation('common');

  const handleLanguageChange = async (language: SupportedLanguage) => {
    if (language !== currentLanguage) {
      await changeLanguage(language);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors text-foreground',
            className
          )}
          aria-label={t('languageSwitcher.select_language')}
        >
          <Languages size={18} className="text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52 p-1.5"
        sideOffset={8}
      >
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground px-2 py-1.5">
          <Languages className="h-3.5 w-3.5" />
          {t('languageSwitcher.language_label')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1.5" />
        {(Object.entries(SUPPORTED_LANGUAGES) as [SupportedLanguage, typeof SUPPORTED_LANGUAGES[SupportedLanguage]][]).map(
          ([code, info]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={cn(
                'flex items-center justify-between gap-3 cursor-pointer rounded-md px-2 py-2',
                'transition-colors duration-150',
                currentLanguage === code
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'hover:bg-accent'
              )}
            >
              <div className="flex items-center gap-3">
                <span
                  className="text-lg flex items-center justify-center w-6"
                  role="img"
                  aria-label={info.name}
                >
                  {info.flag}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm">{info.nativeName}</span>
                  <span className="text-xs text-muted-foreground">{info.name}</span>
                </div>
              </div>
              {currentLanguage === code && (
                <Check className="h-4 w-4 text-primary shrink-0" />
              )}
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
